import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Users, Target, Rocket, Layers, LayoutList, FileText, Clock, Command } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import api from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';

// Entity type icons
const TYPE_ICONS = {
  persona: Users,
  goal: Target,
  initiative: Rocket,
  epic: Layers,
  feature: LayoutList,
  file: FileText
};

// Entity type labels
const TYPE_LABELS = {
  persona: 'Personas',
  goal: 'Goals',
  initiative: 'Initiatives',
  epic: 'Epics',
  feature: 'Features',
  file: 'Files'
};

// Entity type colors
const TYPE_COLORS = {
  persona: 'text-purple-500',
  goal: 'text-green-500',
  initiative: 'text-orange-500',
  epic: 'text-blue-500',
  feature: 'text-indigo-500',
  file: 'text-gray-500'
};

// Result item component - defined outside to avoid re-creation
const ResultItem = ({ result, isSelected, onClick, textPrimaryClass }) => {
  const Icon = TYPE_ICONS[result.type || result._type] || FileText;
  const typeColor = TYPE_COLORS[result.type || result._type] || 'text-gray-500';
  
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-start gap-3 px-3 py-2 text-left rounded-md transition-colors",
        isSelected ? "bg-blue-50 dark:bg-blue-900/30" : "hover:bg-gray-50 dark:hover:bg-gray-800"
      )}
    >
      <Icon className={cn("w-4 h-4 mt-0.5 flex-shrink-0", typeColor)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn("text-sm font-medium truncate", textPrimaryClass)}>
            {result.title}
          </span>
          {result.status && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">
              {result.status}
            </span>
          )}
        </div>
        {result.parent_title && (
          <span className="text-xs text-gray-400 truncate block">
            in {result.parent_title}
          </span>
        )}
        {result.description && !result.parent_title && (
          <span className="text-xs text-gray-400 truncate block">
            {result.description}
          </span>
        )}
      </div>
    </button>
  );
};

const GlobalSearch = ({ 
  productId, 
  onSelectResult,
  className 
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const debouncedQuery = useDebounce(query, 300);
  
  // Flatten results for keyboard navigation
  const flatResults = results ? [
    ...results.personas.map(r => ({ ...r, _type: 'persona' })),
    ...results.goals.map(r => ({ ...r, _type: 'goal' })),
    ...results.initiatives.map(r => ({ ...r, _type: 'initiative' })),
    ...results.epics.map(r => ({ ...r, _type: 'epic' })),
    ...results.features.map(r => ({ ...r, _type: 'feature' })),
    ...results.files.map(r => ({ ...r, _type: 'file' }))
  ] : [];
  
  // Keyboard shortcut to open search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
        setResults(null);
        setSelectedIndex(-1);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);
  
  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  
  // Fetch recent searches
  const fetchRecentSearches = useCallback(async () => {
    if (!productId) return;
    try {
      const response = await api.get(`/api/products/${productId}/search/recent`);
      setRecentSearches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch recent searches:', error);
    }
  }, [productId]);
  
  // Fetch recent searches when opening
  useEffect(() => {
    if (isOpen && productId && !query) {
      fetchRecentSearches();
    }
  }, [isOpen, productId, query, fetchRecentSearches]);
  
  // Perform search
  const performSearch = useCallback(async (searchQuery) => {
    if (!productId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/products/${productId}/search`, {
        params: { q: searchQuery, limit: 5 }
      });
      setResults(response.data);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Search failed:', error);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);
  
  // Search when query changes
  useEffect(() => {
    if (debouncedQuery.length >= 2 && productId) {
      performSearch(debouncedQuery);
    } else {
      setResults(null);
    }
  }, [debouncedQuery, productId, performSearch]);
  
  const handleSelectResult = async (result) => {
    // Record selection for recent searches
    try {
      await api.post(`/api/products/${productId}/search/recent`, null, {
        params: {
          entity_id: result.id,
          entity_type: result.type || result._type,
          entity_title: result.title
        }
      });
    } catch (error) {
      console.error('Failed to record search selection:', error);
    }
    
    // Close search
    setIsOpen(false);
    setQuery('');
    setResults(null);
    setSelectedIndex(-1);
    
    // Notify parent
    onSelectResult?.({ ...result, type: result.type || result._type });
  };
  
  const handleSelectRecent = (recent) => {
    handleSelectResult({
      id: recent.id,
      type: recent.type,
      title: recent.title
    });
  };
  
  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;
    
    const items = query.length >= 2 && results ? flatResults : recentSearches;
    const maxIndex = items.length - 1;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, maxIndex));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex <= maxIndex) {
          const selected = items[selectedIndex];
          if (query.length >= 2 && results) {
            handleSelectResult(selected);
          } else {
            handleSelectRecent(selected);
          }
        }
        break;
      default:
        break;
    }
  };
  
  // Render results grouped by type
  const renderGroupedResults = () => {
    if (!results) return null;
    
    const groups = [
      { key: 'personas', items: results.personas, label: TYPE_LABELS.persona },
      { key: 'goals', items: results.goals, label: TYPE_LABELS.goal },
      { key: 'initiatives', items: results.initiatives, label: TYPE_LABELS.initiative },
      { key: 'epics', items: results.epics, label: TYPE_LABELS.epic },
      { key: 'features', items: results.features, label: TYPE_LABELS.feature },
      { key: 'files', items: results.files, label: TYPE_LABELS.file }
    ].filter(g => g.items.length > 0);
    
    if (groups.length === 0) {
      return (
        <div className="px-3 py-8 text-center">
          <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className={cn("text-sm", colors.textMuted)}>No results found for &ldquo;{query}&rdquo;</p>
        </div>
      );
    }
    
    let globalIndex = -1;
    
    return groups.map((group) => (
      <div key={group.key} className="mb-2">
        <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
          {group.label}
        </div>
        {group.items.map((item) => {
          globalIndex++;
          const currentIndex = globalIndex;
          return (
            <ResultItem
              key={item.id}
              result={{ ...item, _type: group.key.slice(0, -1) }}
              isSelected={selectedIndex === currentIndex}
              onClick={() => handleSelectResult({ ...item, type: group.key.slice(0, -1) })}
              textPrimaryClass={colors.textPrimary}
            />
          );
        })}
      </div>
    ));
  };
  
  return (
    <div className={cn("relative w-full max-w-md", className)} ref={dropdownRef}>
      {/* Search Trigger Button - wider for ~33% of header */}
      <button
        type="button"
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className={cn(
          "w-full flex items-center gap-3 px-4 py-2 rounded-lg border transition-colors",
          "bg-white/50 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800",
          colors.borderPrimary,
          isOpen && "ring-2 ring-blue-500"
        )}
      >
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="flex-1 text-sm text-gray-400 text-left">Search personas, goals, epics, features...</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 dark:bg-gray-700 rounded flex-shrink-0">
          <Command className="w-2.5 h-2.5" />K
        </kbd>
      </button>
      
      {/* Search Dropdown */}
      {isOpen && (
        <div 
          className={cn(
            "absolute top-full left-0 right-0 mt-2 rounded-xl shadow-2xl border overflow-hidden z-50",
            colors.bgCard, colors.borderPrimary
          )}
        >
          {/* Search Input */}
          <div className={cn("flex items-center gap-2 px-3 py-2 border-b", colors.borderPrimary)}>
            <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search personas, goals, epics, features..."
              className="flex-1 border-0 bg-transparent focus:ring-0 pl-1 pr-0 text-sm"
              autoFocus
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setResults(null);
                  inputRef.current?.focus();
                }}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-3 h-3 text-gray-400" />
              </button>
            )}
            {loading && (
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            )}
          </div>
          
          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto py-2">
            {query.length >= 2 && results ? (
              renderGroupedResults()
            ) : (
              // Recent Searches
              <>
                {recentSearches.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 flex items-center justify-between">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Recent
                      </div>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await api.delete(`/api/products/${productId}/search/recent`);
                            setRecentSearches([]);
                          } catch (error) {
                            console.error('Failed to clear recent searches:', error);
                          }
                        }}
                        className="text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-0.5 px-1 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Clear recent searches"
                      >
                        <X className="w-3 h-3" />
                        Clear
                      </button>
                    </div>
                    {recentSearches.slice(0, 5).map((recent, idx) => {
                      const Icon = TYPE_ICONS[recent.type] || FileText;
                      const typeColor = TYPE_COLORS[recent.type] || 'text-gray-500';
                      
                      return (
                        <button
                          key={`${recent.id}-${idx}`}
                          type="button"
                          onClick={() => handleSelectRecent(recent)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors",
                            selectedIndex === idx ? "bg-blue-50 dark:bg-blue-900/30" : "hover:bg-gray-50 dark:hover:bg-gray-800"
                          )}
                        >
                          <Icon className={cn("w-4 h-4", typeColor)} />
                          <span className={cn("text-sm truncate", colors.textPrimary)}>
                            {recent.title}
                          </span>
                          <span className="text-[10px] text-gray-400 capitalize">
                            {recent.type}
                          </span>
                        </button>
                      );
                    })}
                  </>
                )}
                {recentSearches.length === 0 && (
                  <div className="px-3 py-6 text-center">
                    <Search className="w-6 h-6 mx-auto mb-2 text-gray-300" />
                    <p className={cn("text-xs", colors.textMuted)}>
                      Type to search across your product
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Footer hint */}
          <div className={cn("px-3 py-2 border-t text-[10px] text-gray-400 flex items-center gap-4", colors.borderPrimary)}>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[9px]">↑↓</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[9px]">↵</kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[9px]">esc</kbd>
              close
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
